import React, {useRef, createRef} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { ResponsiveLine } from '@nivo/line'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import moment from 'moment'
import Select from 'react-select';
import CountUp from 'react-countup'
import stats from './countriesStats'
import translation from './translations'
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

import smoothscroll from 'smoothscroll-polyfill';


const colors = {
  confirmed: "#1adef1",
  deaths: "#d51732",
  recovered: "#B2CCFF"
}

const toolTipfields = ["confirmed", "deaths", "recovered"]
const t = translation.t;

const params = new URLSearchParams(window.location.search);
let lang = "he";

if (lang) {
  translation.setLanguage(lang);
  
  if (lang == 'he') {
    moment.locale('he');
    moment.updateLocale('he', {
      months : [
        "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי",
        "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"
      ],
      longDateFormat: {
        LL: "D [ב]MMMM"
      }
    });

  }
}

const modelParams = {
  "rSusceptible": {default: 1, min: 0, max: 1, isPercent: true, accuracy: 1000},
  "rMeasureError" : {default: 10, min: 1, max: 50, accuracy: 100, prefix: t('פי ')},
  "rDeath" : {default: 0.008, min: 0, max: 0.1, isPercent: true},
  "rRecoveryDays" : {default: 5, min: 1, max: 10},
  "rInfectionRate" : {default: 0.7, min: 0.2, max: 4}

   //"rInfectionDays" : {default: 1.428, min: 1, max: 5},
  //"daysCount": {default: 100, min: 0, max: 500, accuracy: 1},
  //"capacity" : {default: 0.0022, min: 0, max: 1, isAdvanced: true, isPercent: true},
  //"rSevereCases" : {default: 0.2, min: 0, max: 1,  isAdvanced: true, isPercent: true},
  //"rOverCapacityDeaths" : {default: 0.1, min: 0, max: 1,isAdvanced: true, isPercent: true}
}

class App extends React.Component {
  chartRef = createRef(null);
 
  constructor(props) {
    super(props);

    this.state = {  
      status: "pending",
      tick: 60,
      tickSpeed: 1000,

      rMeasureError: 10,
      rDeath: 0.008,
      rOverCapacityDeaths: 0.1,
      rSevereCases: 0.2,
      capacity: 0.0022,

      //https://www.ijidonline.com/article/S1201-9712(20)30117-X/fulltext#bib0095
      rRecoveryDays: 5, // 1/Days
      rInfectionRate: 0.7, //  0.5944 - 1.68;

      daysCount: 1000,
      rSusceptible: 0.15,

      selectedCountry: "Israel",

      modelEnhanced: false
    }

  }

  calcModelSeries = () => {
    if (!this.state.selectedCountry || !this.state.placesData) return;
   
    let currRows = this.state.placesData[this.state.selectedCountry].find(s=>s.id=='deaths').data;
    let lastRow = currRows[currRows.length - 1];
    let P = stats["PopTotal"][this.state.selectedCountry] * 1000;

    
    let row = {
      P: P,
      S: 1 * this.state.rSusceptible,
      I: (lastRow['confirmed'] * this.state.rMeasureError)/P,
      R: ((lastRow['recovered']||0)+(lastRow['deaths']||0))/P,
      ...lastRow
    }

    //console.log("first Row: ", row);

    let futureRows = [];
    for (var i=0; i<this.state.daysCount; i++) {
      let lastRow = (i==0) ? row : futureRows[i-1];

      let nextRow = this.runModelIteration(lastRow);
      if (nextRow==null) break;

      futureRows.push(nextRow);

      if (lastRow['confirmed'] == 0 && nextRow['confirmed'] == 0) break;
    }
    
    return [
      //{id: "recovered", data: currRows.concat(futureRows.map(f=>{return {...f, y:f['recovered']}}))},
      {id: "deaths", data: currRows.concat(futureRows).map(f=>{return {...f, y:f['deaths']}})},
      {id: "confirmed", data: currRows.concat(futureRows).map(f=>{return {...f, y:f['confirmed']}})}
    ]

  }

  filterModel() {
    let series = this.calcModelSeries();
    if (!series) return {};

    let filteredModel = {};

    let seriesInfectedRaw = series.find(s=>s.id=="confirmed").data; 
    
    filteredModel.endTick = seriesInfectedRaw.indexOf(seriesInfectedRaw.find(row=>row['confirmed'] === 0 && row['I']<0.01));
    filteredModel.endTick = (filteredModel.endTick==-1) ? seriesInfectedRaw.length : filteredModel.endTick;

    filteredModel.series = series.map(serie=>{
      return {
        ...serie,
        data: serie.data.filter((row,i)=> i<this.state.tick && i<= (filteredModel.endTick+1))
      }
    })

    let seriesFatality = filteredModel.series.find(s=>s.id=="deaths").data;
    filteredModel.fatality = {
      start: seriesFatality[seriesFatality.length - 2]['deaths'],
      end: seriesFatality[seriesFatality.length - 1]['deaths']
    }

    let seriesInfected = filteredModel.series.find(s=>s.id=="confirmed").data; 
    let totalInfected =  Math.max(...seriesInfected.map(r=>r['confirmed']).filter(v=>v!=null));
    filteredModel.infectedMax = {
      start: Math.max(...seriesInfected.map(r=>r['confirmed']).filter(v=>v!=null&&v!=totalInfected)) || 0,
      end: totalInfected
    }

    filteredModel.infected = {
      start: seriesInfected[seriesInfected.length - 2]['confirmed'],
      end: seriesInfected[seriesInfected.length - 1]['confirmed']
    }

    filteredModel.maxDeaths = Math.max(...seriesFatality.map(r=>r['deaths']).filter(v=>v!=null)) || 0;

    let currDate = moment(seriesFatality[seriesFatality.length - 1]["date"], "YYYY-M-D");
    filteredModel.currDateDuration = Math.floor(moment.duration(currDate.diff(moment())).as('days')) + t("Days");
    filteredModel.currDate = currDate.format("LL");

    filteredModel.hasEnded = (this.state.tick >= filteredModel.endTick);
    
    let pastRows = series.find(s=>s.id=="confirmed").data.filter(row=>typeof(row['I'])=='undefined');
    filteredModel.lastUpdateDate = moment(pastRows[pastRows.length-1]['x'], "YYYY-M-D").format("DD/MM/YYYY");
    
      //console.log(filteredModel);
    
    //moment.locale(translation.getLanguage, momentHE);
    //filteredModel.currDate = moment(seriesFatality[seriesFatality.length - 1]["date"], "YYYY-M-D").format("MMMM Do");
    
    
    return filteredModel;
  }

  runModelIteration(currRow) {
    //console.log("prevrow:", currRow);
    let {S,I,R,P} = currRow;
    
    //let beta = 1/this.state.rInfectionDays;
    let beta = this.state.rInfectionRate;
    let gamma = 1/this.state.rRecoveryDays;

    var dS = -beta * S * I;
    var dR = gamma * I;
    var dI = -(dS+dR);

    // Each day (1/5) of infected are recovered
    // Each day the infected will infect (0.7) of the suseptible population

    
    //if (S==0) return null; // over population limit

    S+=dS;
    R+=dR;
    I+=dI;

    S = Math.max(S,0);

    let nextRow = {
      ...currRow,
      S:S,
      R:R,
      I:I
    };
    
    nextRow['x'] = moment(currRow['x'], "YYYY-M-D").add(1, "day").format("YYYY-M-D");
    nextRow['date'] = nextRow['x'];

    nextRow['confirmed'] = I*P / this.state.rMeasureError;

    // deaths can't be lower than it is
    let deathCandidate = (this.state.rDeath*R)*P;
    let recoveredCandidate = ((1-this.state.rDeath)*R)*P;

    //if (deathCandidate > currRow['deaths']) {
      nextRow['deaths'] = deathCandidate;
      nextRow['recovered'] = recoveredCandidate;
    //}

    if (this.state.modelEnhanced) {
      if (I*this.state.rSevereCases < this.state.capacity) {
        nextRow['deaths'] = (this.state.rOverCapacityDeaths*R)*P;
        nextRow['recovered'] = ((1-this.state.rOverCapacityDeaths)*R)*P;
      }
    }
    
    //console`.log("nextRow is:", nextRow);
    for (var f in nextRow) {
      nextRow[f] = toolTipfields.includes(f) ? Math.floor(nextRow[f]) : nextRow[f];
    }

    return nextRow;
  }

  componentWillUnmount() {
    clearInterval(this.timer)
  }

  fetchData(url, mode) {
    return fetch(url).then(function(response) {
      
      if (!response.ok && mode == "live") {
        return this.fetchData("timeseries.json", "backup");
      }

      return response;

    }).catch(err => {

      return this.fetchData("timeseries.json", "backup");

    })
  }

  componentDidMount() {

    this.fetchData("https://pomber.github.io/covid19/timeseries.json", "live")
      .then(response => response.json())
      .then(data => {

        
        let placesData = {};
        for (var i in data) {
          placesData[i] = toolTipfields.map(field=>{
            return {
              id: field,
              data: data[i].map(row=>{
                return {
                  ...row,
                  x: row['date'],
                  y: row[field]||null
                }
              })
            }
          })
        }
        
        //let firstDate = moment(data[this.state.selectedCountry][0].date, "YYYY-M-D");
        //let tickStart = Math.floor(moment.duration(currDate.diff(moment())).as('days')) + t("Days");
        let tickStart = data[this.state.selectedCountry].length;

        this.setState({placesData: placesData, countries: Object.keys(data), tick: tickStart, startTick: tickStart});
      })

    let country = params.get('country');
    if (country) {
      this.onChangeCountry({value:country});
    }
    else {
      this.onChangeCountry({value:this.state.selectedCountry});
    }

    smoothscroll.polyfill();

  }

  getChartProps(yScaleMax, lastUpdateDate) {

    let chartProps = {
      //animate:false,
      //motionDamping: 30,

      xScale:{
        type: 'time',
        format: '%Y-%m-%d',
        precision: 'day'
      },
      xFormat:"time:%Y-%m-%d",
      axisBottom:{
        format:  (this.state.tick>100) ? '%b' : '%b %d',
        tickValues: (this.state.tick>100) ? 'every 1 month' : 'every 3 weeks'
      },

      yScale:{ type: 'linear', min: 0, max: yScaleMax, stacked: false },
      axisLeft: {format: (value) => {
        if (value > 1000000) {
          return Math.floor(value/1000000) + "M";
        }
        if (value > 1000) {
          return Math.floor(value/10)/100 + "K";
        }

        return value;
      }},

      margin:{top: 50, bottom: 60, left: 50, right: 0},
      isInteractive:true,
      enableArea:true,
      areaOpacity: 1,
      lineWidth: 0,
      useMesh:true,
      enablePoints:false,
      //enableCrosshair:false,
      enableSlices:'x',
      //curve:"step",
      colors:(serie)=>{
        return colors[serie.id] ? colors[serie.id] : "grey";
      },
      sliceTooltip:(tooltip)=>{
        let item = tooltip.slice.points[0];
        if (!item.data['date']) return <></>;

        return <div className="card p-2 bg-dark text-white">
          <b>{moment(item.data['date'], "YYYY-M-D").format("DD/MM/YYYY")}</b>
          
          {toolTipfields.map(f=>(
            <div key={f}>{t(f)}: {Math.floor(item.data[f]).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</div>
          ))}

        </div>
      }
      
    }

    if (this.state.modelEnhanced) {
      chartProps.markers =[
        {
            axis: 'y',
            value: (stats["PopTotal"][this.state.selectedCountry] * 1000 * this.state.capacity),
            lineStyle: { stroke: '#b0413e', strokeWidth: 3 },
            legend: t('capacity'),
            legendOrientation: 'vertical',
        }
      ];
    }

    chartProps.markers =[
      {
          axis: 'x',  
          value: moment(lastUpdateDate, "DD/MM/YYYY").toDate(),
          lineStyle: { stroke: '#d51732', strokeWidth: 3, transition: ".5s" },
          legend: t('תחזית'),
          legendPosition: 'right'
      },
      {
        axis: 'x',  
        value: moment(lastUpdateDate, "DD/MM/YYYY").toDate(),
        lineStyle: { stroke: '#d51732', strokeWidth: 3, transition: ".5s"  },
        legend: t('נתוני אמת'),
        legendPosition: 'left'
    }
    ];

    chartProps.layers = ['grid', 'axes', 'areas', 'markers', 'crosshair', 'lines', 'points', 'slices', 'mesh', 'legends'];

    return chartProps;
  }

  startSimulation = () => {

    this.timer = setInterval(()=>{

      //if (this.state.tick >= this)

      if (this.state.status == "stopped") {
        // show max
        clearInterval(this.timer);
        return;
      }

      this.setState({tick: ++this.state.tick});
    }, this.state.tickSpeed)

    this.setState({status: "playing"});

  }

  getSliderProps() {
    let s = modelParams.rSusceptible;

    let sliderProps = {

      min: 0, 
      max: 1000,
      //defaultValue: 150,
      value: this.state.rSusceptible * s.accuracy,
      onChange:(v)=>{this.setState({rSusceptible: v / s.accuracy, sliderHasMoved: true})},
      reverse:translation.getLanguage() == 'he',
      marks:{
        0: <div className="marker marker-s1" dangerouslySetInnerHTML={{__html: t('BTN_R0_LOW')}}></div>,
        500: <div className="marker marker-s2" dangerouslySetInnerHTML={{__html: t('BTN_R0_MEDIUM')}}></div>,
        1000: <div className="marker marker-s3" dangerouslySetInnerHTML={{__html: t('BTN_R0_HIGH')}}></div>
      },

      trackStyle:{ height: 20,  background: colors.confirmed },
      handleStyle:{      
        height: 60,
        width: 60,
        marginTop: -20,
        //marginRight: 12,

        //borderColor: colors.confirmed,
        //border: "1px solid black",
        boxShadow: "0 10px 10px 0 rgba(0,0,0,.5)",
        border: "none",
        background: "url('images/gnew.png') no-repeat center center",
        backgroundSize: "cover"
      },
      railStyle:{ height: 20},
      dotStyle: {height: 30, width: 10, marginBottom: -20, borderRadius: 0},
      activeDotStyle: {height: 30, width: 10, marginBottom: -20, borderColor: colors.confirmed}

    }

    return sliderProps; 
  }

  renderSlider = (p) => {
    let accuracy = modelParams[p].accuracy || 1000;
    return <div key={p} className="px-2 my-4 w-100">
        
        <div className="d-flex" style={{justifyContent: "space-between"}}>

          <span><b> {t(p)}</b> </span>

          <span>
            {modelParams[p].prefix ? modelParams[p].prefix : ""}
            {this.state[p] && modelParams[p].isPercent ? ((Math.floor(this.state[p]*100*100))/100 + "%") : this.state[p]}</span>
        </div>
        
        <Slider 
          min={modelParams[p].min* accuracy} 
          max={modelParams[p].max* accuracy}
          defaultValue={modelParams[p].default * accuracy}
          value={this.state[p] * accuracy}
          onChange={(v)=>{this.setState({[p]: v / accuracy})}}

          trackStyle={{ height: 10 }}
          handleStyle={{
            height: 20,
            width: 20,
            marginTop: -5,
            marginLeft: 5,
            marginRight: -5
          }}
          railStyle={{ height: 10 }}
          />  

          <div className="mt-3 small"><i className="fas fa-info-circle"></i>  {t(p+"_desc")}</div>
    </div>
  }

  onChangeCountry = (v) => {
    let shouldSetToDefault = false;
    if (stats["CustomReportedRatio"][this.state.selectedCountry] && this.state.rMeasureError == stats["CustomReportedRatio"][this.state.selectedCountry]) {
      shouldSetToDefault = true;
    }

    window.history.replaceState({}, '', '?lang='+translation.getLanguage()+'&country='+v.value); 
    this.setState({selectedCountry: v.value});
    if (stats["CustomReportedRatio"][v.value]) {
      this.setState({rMeasureError: stats["CustomReportedRatio"][v.value]});
    }
    else if (shouldSetToDefault) {
      this.setState({rMeasureError: modelParams.rMeasureError.default});
    }
  }

  render() {
    
    let model = this.filterModel();
    let series = model.series;
    
    return  <div className={(this.state.showSiderbar ? "siderbar-open" : "sidebar-closed") + " status-" + this.state.status}>

    <div className="content-wrapper">

    <div className="card  question" id="question" style={{height: (this.state.status == 'pending' ? '100vh' : 'auto')}}>

          {this.state.status == 'pending' && <div className="text-center">
            <a href='https://globes.co.il' target='_blank'><img src="images/logo.png" className="img-fluid logo-image" /></a>
          </div>}

          <h1 className="text-center px-4">{t('TITLE_QUESTION')}</h1>

          <div className="question-slider-wrapper">
            <div style={{height:"100px"}} className="mt-3 question-slider">
              <Slider {...this.getSliderProps()} />  
            </div>
          </div>

          <div className="text-center py-2 px-4 stat-description"> 
              {this.state.sliderHasMoved && <>{t('POPULATION_EXPOSED_PRE')} {((Math.floor(this.state.rSusceptible*100)) + "%")} {t('POPULATION_EXPOSED_POST')}</>}
              {!this.state.sliderHasMoved && <>{t('slider_instructions')}</>}

              
          </div>

          
          <div className="btns-wrapper d-flex text-center px-5">
              {this.state.status == 'pending' && <div className="btn btn-dark btn-lg" onClick={()=>{
                this.startSimulation();
                this.setState({sliderHasMoved: true})

                if (window.innerHeight < 900) {
                  setTimeout(()=> {
                    //window.scrollTo(0, this.chartRef.current.offsetTop*0.6);
                    window.scroll({ top: this.chartRef.current.offsetTop*0.6, left: 0, behavior: 'smooth' });

                  },1000);
                }

            }}>{t('PLAY')}</div>}
          </div>

        </div>

      <div className="container content"  ref={this.chartRef} >
        
        <div className="container-fluid"  >
          <div className={"row " + (model.fatality&&(model.fatality.end>100000||model.infected.end>100000||model.infectedMax.end>1000000) ? "long" : "")} >
             
            <div className="col-6 stat-wrapper stat-wrapper-deaths">
              <div className="text-center p-0 m-0 ">
                
                <div className="stat">
                  {model.fatality&&<CountUp duration={this.state.tickSpeed/1000} useEasing={false} separator=","  {...model.fatality}/>}
                </div>
                <label style={{background:colors.deaths}}>{t('DEATH_COUNT')}</label>
              </div>
            </div>
            
            <div className="col-6 stat-wrapper stat-wrapper-infected">
              <div className="text-center p-0 m-0">
                
              <div className="stat">
                  {false && !model.hasEnded && model.infected&&<CountUp duration={this.state.tickSpeed/1000} useEasing={false} separator=","  {...model.infected}/>}
                  {model.infectedMax&&<CountUp duration={this.state.tickSpeed/1000} useEasing={false} separator=","  {...model.infectedMax}/>}
                </div>
                <label style={{background:colors.confirmed}}>{(model.infected&&model.infected.end>=model.infectedMax.end) ? t('INFECTED_COUNT') : t('INFECTED_COUNT_MAX')} </label>
              </div>

            </div>

            <div className="chart-area">
                <div className="counter-date text-center" >
                  <div className="px-1">{this.state.dateText||model.currDate}</div>
                </div>
                
              <div className="chart-wrapper">
                {series && <ResponsiveLine {...this.getChartProps((this.state.zoom && this.state.zoom == 'deaths') ? model.maxDeaths : 'auto', model.lastUpdateDate)} data={series}  />}
              </div>
            </div>
           
            

          </div>
        </div>

        {this.state.status != 'pending0' && <div className="my-3 text-center">
          {<div className="btn btn-dark mx-1 mt-2 border-0" onClick={()=>{this.setState({tick: this.state.startTick, status: "stopped"})}}><i className="fas fa-undo"></i></div>}


          {<div className="btn btn-dark mx-1 mt-2 border-0" disabled={true} onClick={()=>{this.setState({tick: this.state.tick+7})}}><i className="fas fa-forward"></i> {t('Skip')}</div>}
          
          {this.state.status == 'playing' && <div className="btn btn-dark mx-1 mt-2 border-0" onClick={()=>{this.setState({status: "stopped"})}}><i className="fas fa-pause"></i> {t('Pause')}</div>}
          {this.state.status != 'playing' &&  <div className="btn btn-dark mx-1 mt-2 border-0" onClick={this.startSimulation}><i className="fas fa-play"></i> {t('Play')}</div>}

        </div>}

        <div className="my-3 text-center">
          {<div className="d-inline-block" style={{width:"150px"}}>{this.state.countries && <Select 
                value={this.state.selectedCountry ? {value: this.state.selectedCountry, label: t(this.state.selectedCountry)} : "Select country"}
                options={this.state.countries
                  .sort((a,b)=>{return ('' + t(a)).localeCompare(t(b))})
                  .map(p=>{return {value:p, label: t(p)}})}
                onChange={this.onChangeCountry}
              />
            }
          </div>}      
        </div>

        <div className="text-center mt-5"><img src='images/arrows.png' width="15" /></div>

        <div className="text-center mt-5"><img src='images/globes-logo-alt.svg' width="70" /></div>
        <h2 className="globes-title mt-0 pt-0">סימולטור הקורונה של גלובס</h2>
        <p className="text-center font-weight-bold">מאת <a href='https://yaronshemesh.com/' target='_blank'>ירון שמש</a></p>

        <p>{t('p1')}</p>

        
          


        <p dangerouslySetInnerHTML={{__html: t('p2')}}></p>

        <div className="text-center mb-3">
          {!this.state.showSiderbar && <div className="btn btn-dark mx-1 mt-2 border-0" onClick={()=>{this.setState({showSiderbar: true})}}><i className="fas fa-sliders-h"></i> {t('EDIT_MODEL')}</div>}
          {this.state.showSiderbar && <div className="btn btn-dark mx-1 mt-2 border-0" onClick={()=>{this.setState({showSiderbar: false})}}><i className="fas fa-sliders-h"></i> {t('HIDE_SIDEBAR_ALT')}</div>}
        </div>
          
        <p dangerouslySetInnerHTML={{__html: t('p3')}}></p>
        <p dangerouslySetInnerHTML={{__html: t('p4')}}></p>        

        <p>*{t('last_data_update')} {model.lastUpdateDate} </p>
        

        <footer>

          
          <p className="small">
          <b className="mt-3">{t('SOURCES')}</b>
              Model values for the covid-19 based on <a target="_blank" href="https://doi.org/10.1016/j.ijid.2020.02.058">https://doi.org/10.1016/j.ijid.2020.02.058</a>. 
              Recent covid-19 statisticts fetched from <a target="_blank" href="https://github.com/pomber/covid19">pomber github</a>, based on <a href="https://github.com/CSSEGISandData/COVID-19">Johns Hopkins CSSE</a>. 
              Populations Estimates: <a target="_blank" href="https://www.un.org/development/desa/en/">United Nations, Department of Economic and Social Affairs</a>.
          </p>
        
          
          
          <p className="my-5 px-2 text-center font-weight-bold">סימולטור הקורונה של גלובס פותח על ידי <a target="_blank" href="https://yaronshemesh.com">ירון שמש</a></p>       

          <p className="text-center">
            <a className="btn btn-success mx-1 text-white border-0" style={{"background":"#25D366"}} target="_blank" href={"https://api.whatsapp.com/send?text="+ encodeURIComponent(window.location.href)}><i className="fab fa-whatsapp"></i> {t('Share')}</a>  
            <a className="btn btn-primary mx-1 text-white border-0"  style={{"background":"#4267B2"}} target="_blank" href={"https://www.facebook.com/dialog/share?app_id=2690420677856175&redirect_uri="+ encodeURIComponent(window.location.href)+"&href="+encodeURIComponent(window.location.href)}><i className="fab fa-facebook-f"></i> {t('Share')}</a>
         </p>

 
        </footer>

      </div>


    </div>

    <div className="sidebar panel" >
      
      <div className="btn btn-dark btn-lg rounded-0 w-100" onClick={()=>{this.setState({showSiderbar: false})}}> &lt; {t('HIDE_SIDEBAR')}</div>

      <div className="px-2">

        <div className="p-2">
            <b>{t("country")}</b>
            {this.state.countries && <Select 
                value={this.state.selectedCountry ? {value: this.state.selectedCountry, label: this.state.selectedCountry} : "Select country"}
                options={this.state.countries.map(p=>{return {value:p, label: p}})}
                onChange={this.onChangeCountry}
              />
            }
            <span className="d-none">{t("country_desc")}</span>
        </div>

        {Object.keys(modelParams).filter(p=>p!='rSusceptible').map(this.renderSlider)}

        <div className="p-2">
          <p>{t('EDIT_MODEL_DESC')}</p>
        </div>
        
      </div>

      
    </div>

    <style jsx="true" global="true">{`
      body {
        background: #ffe6d9;
        color: #222;

      }

      h1 {
        font-weight: 700;
      }

      .globes-title {
        text-align: center;
        margin: 1em;
        font-weight: 700;
        font-size: 2.1em;
      }

      .arrow_box {
        position: relative;
        background: #0000009e;
      }
      .arrow_box:after {
        left: 100%;
        top: 50%;
        border: solid transparent;
        content: " ";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        border-color: rgba(0, 0, 0, 0);
        border-left-color: #0000009e;
        border-width: 10px;
        margin-top: -10px;
      }

      .question {
        background: #951A40 url('images/bkg2.svg');
        color: white;
        
        display: flex;
        flex-direction: column;
        
        margin-bottom: 1em;     
        justify-content: normal;
        padding-top:1em;
        padding-bottom:1em;

        transition: 1s;

        border-radius: 0;
      }

      

      .status-pending .question {
        width: 100%;
        justify-content: space-evenly;
        padding-bottom: 3em;
      }

      .content {
        max-width: 800px;
      }
      
      .rc-slider-mark {
        top: 50px;

      }

      .rc-slider-handle {
        z-index: 1000;
      }

      .marker {
        color: white; transition: .5s; 
        font-size: 1.5em; 
        background: #85002e; 
        padding: 5px;
        line-height: 100%;
        padding-right: 30px;
      }
      
      .status-pending .rc-slider-mark-text {
        font-size: 1.5em;
      }
      
      .marker-s1 {
        background: #85002e url('images/s1.png') no-repeat right center;
        background-size: 28px;
        margin-left: -20px;
      }

      .marker-s2 {
        background: #85002e url('images/s2.png') no-repeat right center;
        background-size: 28px;
      }

      .marker-s3 {
        background: #85002e url('images/s3.png') no-repeat right center;
        background-size: 28px;
        margin-right: -20px;
      }

      .question .btn {
        border-radius: 10px;
        width: 100%;
      }

      .stat {
        font-size: 2.8em;
        font-weight: 900;
        cursor: pointer;
        transition: 1s;
      }

      .long .stat {
        font-size: 2em;
      }

      .stat-wrapper label {
        color: #fff;
        padding: 8px 45px;
        cursor: pointer;
        border-radius: 10px;
        font-size: 1.3em;
      }

      .stat-wrapper-infected label {
        color: #000;
      }

      .counter-date {
        position: absolute;
        top: 30px;
        background: #0000009e;
        z-index: 99;
        margin: auto;
        color: white;
        font-size: 1.3em;
        transition: 1s;
        font-weight: 700;
        padding: 0px 4px;

        border-radius: 5px;
      }

      .counter-date .btn {
        background: #dc5a32;
        border-radius: 10px;
      }

      .counter-date {
        right: 50%;
        margin-right: -75px;
        width: 150px;      
      }

      .panel {
        flex-wrap: wrap;
        justify-content: center;
      }

      .sidebar {
        position: fixed;
        top:0;
        width: 0;
        overflow: hidden;
        transition: .5s;
        box-shadow: 0 10px 60px 0 rgba(0,0,0,.2);
        background: #fff;
        height: 100%;

        left:0;
        z-index: 1060;
      }

      .siderbar-open .sidebar {
        
        width: 20vw;
        max-width: 100%;
        
        overflow-y: auto;
        overflow-x: hidden;

      }

      .siderbar-open .content-wrapper {
        margin-left: 20vw;
      }

      .content-wrapper {
        margin-left: 0;
        transition: .5s;
      }

      .chart-area, .chart-wrapper {
        width: 100%;
        height: 300px;
      }

      .chart-area {
        position: relative;
      }

      .stat-description {font-size: 1.5em; margin-top: 2em; font-weight: 500;}


      .status-pending .stat-description {
        font-size: 1.7em;
        margin-top: 0;
        line-height: 1em;
      }

      
      .btns-wrapper .btn {
        min-height: 2.5em;
        line-height: 2.5em;
        max-width: 400px;
        margin: 0 auto;

        font-weight: 300;
        font-size: 2.5em;
      }

      .question-slider-wrapper {
        padding: 0 3em;
      }

      @media only screen and (min-width: 600px) {
        .logo-image {
          width: 80px;
        }
        .question-slider-wrapper {
          margin: 0 auto;
        }

        .question-slider {
          width: 500px;
        }

        .stat-description {
          font-size: 2em;
        }

        
      }

      @supports (-webkit-touch-callout: none) {
        .question-slider-wrapper {
          padding: 0 5em;
          user-select: none;
        }
      }

      @media only screen and (max-width: 600px) {
        .logo-image {
          width: 65px;
        }

        .question h1 {
          font-size: 3em;
        }

        .siderbar-open .sidebar {
          width: 100%;
        }

        .siderbar-open .content-wrapper {
          height: 90vh;
          overflow: hidden;
        }

        .btns-wrapper .btn {
          font-size: 1.8em;
        }
      }

      @media only screen and (max-width: 400px) {
        .question {
          font-size: .7em;
        }

        .marker {
          font-size: 1em;
        }

        .btns-wrapper .btn {
          font-size: 1.8em;
        }

      }

    `}</style>

    {translation.getLanguage() == "he" && <style jsx="true" global="true">{`
      html,body, .card {direction: rtl; text-align: right;}
      .chart-wrapper {direction: ltr; text-align:left;}

      .sidebar {right:0; left: auto;}
      .siderbar-open .content-wrapper {margin-right: 20vw; margin-left: auto;}
      .content-wrapper {margin-right: 0; margin-left: auto;}

    `}</style>}

    </div>
  }
  
}

export default App;
